import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColumnState, ModuleRegistry, ClientSideRowModelModule, ValidationModule, QuickFilterModule, PaginationModule, RowStyleModule, TextFilterModule, GridApi, GridReadyEvent, ColumnResizedEvent, ColumnMovedEvent, FilterChangedEvent } from 'ag-grid-community';
import { Box, Paper, useTheme, TextField, InputAdornment, IconButton, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Chip } from '@mui/material';
import { ClearGridFiltersButton, useColumnFiltersActive } from './ClearGridFiltersButton';
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, Folder as FolderIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { Project, ProjectType, User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import { PROJECT_TYPE_PROPOSAL } from '../utils/projectTypes';
import { projectsService } from '../services/projectsService';
import { localStore } from '../utils/localStore';
import { isUserOnProjectTeam } from '../utils/projectTeam';

// Register AG Grid modules
ModuleRegistry.registerModules([ClientSideRowModelModule, ValidationModule, QuickFilterModule, PaginationModule, RowStyleModule, TextFilterModule]);

const PROJECTS_TABLE_STATE_KEY = 'projects_management_table_state_v2';
const PROJECTS_TABLE_COLUMN_WIDTHS_KEY = 'projects_management_table_column_widths_v1';
const PROPOSALS_TABLE_STATE_KEY = 'proposals_management_table_state_v1';

export type ProjectsTableScope = 'standard' | 'proposal';

const isClosedStatus = (status?: string | null) =>
    (status || '').trim().toLowerCase() === 'closed';

const isLegacyClosedExclusionFilter = (statusModel: unknown) => {
    if (!statusModel || typeof statusModel !== 'object') return false;
    const model = statusModel as { type?: string; filter?: string };
    return model.type === 'notEqual' && isClosedStatus(model.filter);
};

const PROGRAMMATIC_COLUMN_RESIZE_SOURCES = new Set([
    'api',
    'sizeColumnsToFit',
    'autosizeColumns',
    'gridOptionsChanged',
    'flex',
]);

// Import AG Grid styles
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

interface ProjectsTablePersistedState {
    columnState?: ColumnState[];
    filterModel?: Record<string, unknown> | null;
    quickFilter?: string;
    projectTypeFilter?: number | '';
    showClosed?: boolean;
}

interface ProjectsTableAgGridProps {
    projects: Project[];
    users: User[];
    onProjectClick: (project: Project) => void;
    onEditClick: (projectId: number) => void;
    scope?: ProjectsTableScope;
}

const ProjectsTableAgGrid: React.FC<ProjectsTableAgGridProps> = ({
    projects,
    users,
    onProjectClick,
    onEditClick,
    scope = 'standard',
}) => {
    const theme = useTheme();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const clientNameFilter = searchParams.get('client')?.trim() ?? '';
    const [quickFilter, setQuickFilter] = useState('');
    const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
    const [projectTypeFilter, setProjectTypeFilter] = useState<number | ''>('');
    const [showClosed, setShowClosed] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi<Project> | null>(null);
    const { columnFiltersActive, syncColumnFilters, clearColumnFilters } = useColumnFiltersActive();
    const hasSavedColumnWidthsRef = useRef(false);
    const savedTableStateRef = useRef<ProjectsTablePersistedState | null>(null);
    const savedStateLoadedRef = useRef(false);
    const restoreCompleteRef = useRef(false);
    const isApplyingSavedStateRef = useRef(false);
    const [savedStateVersion, setSavedStateVersion] = useState(0);
    const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isProposalScope = scope === 'proposal';
    const tableStateKey = isProposalScope ? PROPOSALS_TABLE_STATE_KEY : PROJECTS_TABLE_STATE_KEY;

    const applySavedColumnStateToApi = useCallback((api: GridApi<Project>, applyOrder = false) => {
        const savedState = savedTableStateRef.current?.columnState;
        if (!savedState?.length) return;

        const state = savedState.map((columnState) => ({
            ...columnState,
            flex: columnState.flex ?? null,
        }));

        isApplyingSavedStateRef.current = true;
        try {
            api.applyColumnState({ state, applyOrder });
            hasSavedColumnWidthsRef.current = savedState.some(
                (columnState) => typeof columnState.width === 'number' && columnState.width > 0
            );
        } finally {
            window.setTimeout(() => {
                isApplyingSavedStateRef.current = false;
            }, 0);
        }
    }, []);

    const applySavedGridStateToApi = useCallback((api: GridApi<Project>) => {
        applySavedColumnStateToApi(api, true);

        const savedState = savedTableStateRef.current;
        isApplyingSavedStateRef.current = true;
        try {
            let filterModel: Record<string, unknown> | null = null;
            if (savedState && 'filterModel' in savedState && savedState.filterModel) {
                filterModel = { ...savedState.filterModel };
                if (isLegacyClosedExclusionFilter(filterModel.status)) {
                    delete filterModel.status;
                }
                if (Object.keys(filterModel).length === 0) {
                    filterModel = null;
                }
            }
            api.setFilterModel(filterModel);
        } finally {
            window.setTimeout(() => {
                isApplyingSavedStateRef.current = false;
            }, 0);
        }
    }, [applySavedColumnStateToApi]);

    const loadPersistedTableState = useCallback(async (): Promise<ProjectsTablePersistedState | null> => {
        try {
            let savedState = await localStore.get(tableStateKey) as ProjectsTablePersistedState | undefined;
            if (!isProposalScope && (!savedState || typeof savedState !== 'object')) {
                const legacyWidths = await localStore.get(PROJECTS_TABLE_COLUMN_WIDTHS_KEY) as Record<string, number> | undefined;
                if (legacyWidths && typeof legacyWidths === 'object') {
                    const columnState = Object.entries(legacyWidths)
                        .filter(([, width]) => typeof width === 'number' && width > 0)
                        .map(([colId, width]) => ({ colId, width, flex: null }));
                    if (columnState.length) {
                        savedState = { columnState };
                    }
                }
            }
            return savedState ?? null;
        } catch {
            return null;
        }
    }, [isProposalScope, tableStateKey]);

    const collectTableState = useCallback((api: GridApi<Project> | null): ProjectsTablePersistedState => ({
        columnState: api?.getColumnState() ?? savedTableStateRef.current?.columnState,
        filterModel: api?.getFilterModel() ?? savedTableStateRef.current?.filterModel ?? null,
        quickFilter,
        projectTypeFilter,
        showClosed,
    }), [quickFilter, projectTypeFilter, showClosed]);

    const persistTableState = useCallback((api: GridApi<Project> | null = gridApi) => {
        if (!restoreCompleteRef.current || !api || isApplyingSavedStateRef.current) {
            return;
        }

        if (persistTimeoutRef.current) {
            clearTimeout(persistTimeoutRef.current);
        }

        persistTimeoutRef.current = setTimeout(async () => {
            try {
                const state = collectTableState(api);
                savedTableStateRef.current = state;
                await localStore.set(tableStateKey, state);
                hasSavedColumnWidthsRef.current = Boolean(
                    state.columnState?.some((columnState) => typeof columnState.width === 'number' && columnState.width > 0)
                );
            } catch {
                // Ignore persistence failures (private mode / blocked storage).
            }
            persistTimeoutRef.current = null;
        }, 400);
    }, [collectTableState, gridApi, tableStateKey]);

    const fitColumnsToViewport = useCallback((api: GridApi<Project>) => {
        if (hasSavedColumnWidthsRef.current) {
            return;
        }
        window.requestAnimationFrame(() => {
            if (hasSavedColumnWidthsRef.current) {
                return;
            }
            try {
                api.sizeColumnsToFit();
            } catch {
                // Ignore if grid has not fully initialized yet.
            }
        });
    }, []);

    useEffect(() => () => {
        if (persistTimeoutRef.current) {
            clearTimeout(persistTimeoutRef.current);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        projectsService.getProjectTypes().then((types) => {
            if (!cancelled) setProjectTypes(types);
        }).catch(() => { /* table still works without type labels */ });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;

        void loadPersistedTableState().then((savedState) => {
            if (cancelled || !savedState) {
                savedStateLoadedRef.current = true;
                setSavedStateVersion((v) => v + 1);
                return;
            }

            savedTableStateRef.current = savedState;

            if (savedState.quickFilter !== undefined) {
                setQuickFilter(savedState.quickFilter);
            }
            if (savedState.projectTypeFilter !== undefined) {
                setProjectTypeFilter(savedState.projectTypeFilter);
            }
            if (savedState.showClosed !== undefined) {
                setShowClosed(Boolean(savedState.showClosed));
            }

            hasSavedColumnWidthsRef.current = Boolean(
                savedState.columnState?.some((columnState) => typeof columnState.width === 'number' && columnState.width > 0)
            );
            savedStateLoadedRef.current = true;
            setSavedStateVersion((v) => v + 1);
        });

        return () => {
            cancelled = true;
        };
    }, [loadPersistedTableState]);

    useEffect(() => {
        if (!restoreCompleteRef.current) {
            return;
        }
        persistTableState();
    }, [quickFilter, projectTypeFilter, showClosed, persistTableState]);

    // Check if user can see budget columns
    const canSeeBudgets = useMemo(() => {
        return hasCapability(user, CAP.VIEW_PROJECT_BUDGETS);
    }, [user]);

    const columnDefs = useMemo<ColDef<Project>[]>(() => {
        const cols: ColDef<Project>[] = [
            {
                colId: 'nd_number',
                headerName: "ND Number",
                field: "nd_number",
                width: 170,
                minWidth: 150,
                flex: 1.1,
                cellRenderer: (params: any) => {
                    const project = params.data;
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FolderIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                            <span 
                                style={{ userSelect: 'auto', cursor: 'text' }} 
                                onMouseDownCapture={(e) => e.stopPropagation()}
                            >
                                {project.nd_number || '-'}
                            </span>
                        </Box>
                    );
                }
            },
            {
                colId: 'name',
                headerName: "Name",
                field: "name",
                width: 250,
                minWidth: 180,
                flex: 1.8,
                cellRenderer: (params: any) => {
                    const project = params.data;
                    return (
                        <Box
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                                fontWeight: 500
                            }}
                            onClick={() => onProjectClick(project)}
                        >
                            {project.name.toUpperCase()}
                        </Box>
                    );
                }
            },
            {
                colId: 'customer',
                headerName: "Customer",
                field: "client_name",
                width: 200,
                minWidth: 150,
                flex: 1.4,
                valueFormatter: (params) => params.value || '-'
            },
            {
                colId: 'office',
                headerName: "Office",
                width: 80,
                minWidth: 80,
                flex: 0.8,
                valueGetter: (params) => {
                    const project = params.data as Project;
                    if (!project) return '-';
                    if (project.office_name && project.office_code) {
                        return project.office_code;
                    }
                    return project.office_name || project.office_code || project.office || project.office_country || '-';
                },
                filter: true
            },
            {
                colId: 'status',
                headerName: "Status",
                field: "status",
                width: 120,
                minWidth: 100,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: {
                    caseSensitive: false,
                },
                valueFormatter: (params) => params.value || '-'
            },
            {
                colId: 'project_start_date',
                headerName: "Start Date",
                field: "project_start_date",
                width: 200,
                minWidth: 140,
                flex: 1.2,
                valueFormatter: (params) => params.value || '-'
            },
            {
                colId: 'project_planned_end_date',
                headerName: "Planned End Date",
                field: "project_planned_end_date",
                width: 200,
                minWidth: 140,
                flex: 1.2,
                valueFormatter: (params) => params.value || '-'
            },
            {
                colId: 'year',
                headerName: "Year",
                field: "year",
                width: 100,
                minWidth: 80,
                flex: 0.8,
                valueFormatter: (params) => params.value || '-'
            }
        ];

        // Add budget columns only if user has permission
        if (canSeeBudgets) {
            cols.push(
                {
                    colId: 'initial_budget',
                    headerName: "Initial Budget",
                    field: "initial_budget",
                    width: 150,
                    minWidth: 120,
                    flex: 1,
                    valueFormatter: (params) => {
                        if (params.value) {
                            return new Intl.NumberFormat().format(params.value);
                        }
                        return '-';
                    }
                },
                {
                    colId: 'nd_budget',
                    headerName: "ND Budget",
                    field: "nd_budget",
                    width: 150,
                    minWidth: 120,
                    flex: 1,
                    valueFormatter: (params) => {
                        if (params.value) {
                            return new Intl.NumberFormat().format(params.value);
                        }
                        return '-';
                    }
                },
                {
                    colId: 'actions',
                    headerName: "Actions",
                    width: 100,
                    minWidth: 90,
                    maxWidth: 120,
                    flex: 0.7,
                    cellRenderer: (params: any) => {
                        const project = params.data;
                        return (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEditClick(project.id);
                                }}
                                title="Configure project"
                            >
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        );
                    },
                    sortable: false,
                    filter: false
                }
            );
        }

        return cols;
    }, [canSeeBudgets, onProjectClick]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true
    }), []);

    // Filter projects based on user role first
    const roleFilteredProjects = useMemo(() => {
        if (!user) return projects;

        if (hasCapability(user, CAP.VIEW_ALL_PROJECTS)) {
            return projects;
        }

        // For other roles, show only projects where user is PM or engineer
        return projects.filter(project => isUserOnProjectTeam(project, user));
    }, [projects, user]);

    const listProjectTypes = useMemo(
        () => projectTypes.filter((pt) => pt.project_type !== PROJECT_TYPE_PROPOSAL),
        [projectTypes],
    );

    useEffect(() => {
        if (isProposalScope) {
            if (projectTypeFilter !== '') setProjectTypeFilter('');
            return;
        }
        if (projectTypeFilter === '') return;
        const selected = projectTypes.find((pt) => pt.id === projectTypeFilter);
        if (selected?.project_type === PROJECT_TYPE_PROPOSAL) {
            setProjectTypeFilter('');
        }
    }, [isProposalScope, projectTypes, projectTypeFilter]);

    const clearClientNameFilter = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('client');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // Then apply project type filter, quick filter, and sort
    const filteredProjects = useMemo(() => {
        let result = roleFilteredProjects.filter((project) => (
            isProposalScope
                ? project.project_type === PROJECT_TYPE_PROPOSAL
                : project.project_type !== PROJECT_TYPE_PROPOSAL
        ));

        if (clientNameFilter) {
            const target = clientNameFilter.toLowerCase();
            result = result.filter(
                (project) => (project.client_name?.trim().toLowerCase() ?? '') === target,
            );
        } else if (!showClosed) {
            result = result.filter((project) => !isClosedStatus(project.status));
        }

        if (projectTypeFilter !== '') {
            result = result.filter((project) => project.project_type_id === projectTypeFilter);
        }

        if (quickFilter.trim()) {
            const lowerFilter = quickFilter.toLowerCase();
            result = result.filter(project =>
                (project.nd_number?.toLowerCase().includes(lowerFilter)) ||
                (project.name?.toLowerCase().includes(lowerFilter)) ||
                (project.client_name?.toLowerCase().includes(lowerFilter)) ||
                (project.office_name?.toLowerCase().includes(lowerFilter)) ||
                (project.office_code?.toLowerCase().includes(lowerFilter)) ||
                (project.office?.toLowerCase().includes(lowerFilter)) ||
                (project.status?.toLowerCase().includes(lowerFilter))
            );
        }

        // Newest year first, then highest project sequence (trailing digits of ND number).
        return [...result].sort((a, b) => {
            const yearA = a.year || 0;
            const yearB = b.year || 0;

            if (yearA !== yearB) {
                return yearB - yearA;
            }

            const getProjNum = (nd: string | undefined) => {
                if (!nd) return 0;
                // ND-XX-26-01008 → 1008
                const match = nd.match(/(\d+)$/);
                return match ? parseInt(match[1], 10) : 0;
            };

            return getProjNum(b.nd_number) - getProjNum(a.nd_number);
        });
    }, [roleFilteredProjects, quickFilter, projectTypeFilter, showClosed, clientNameFilter, isProposalScope]);

    const onGridReady = useCallback(async (params: GridReadyEvent<Project>) => {
        try {
            if (!savedStateLoadedRef.current) {
                const savedState = await loadPersistedTableState();
                if (savedState) {
                    savedTableStateRef.current = savedState;
                    hasSavedColumnWidthsRef.current = Boolean(
                        savedState.columnState?.some((columnState) => typeof columnState.width === 'number' && columnState.width > 0)
                    );
                }
            }

            applySavedGridStateToApi(params.api);
            if (!hasSavedColumnWidthsRef.current) {
                fitColumnsToViewport(params.api);
            }
        } finally {
            savedStateLoadedRef.current = true;
            restoreCompleteRef.current = true;
            setSavedStateVersion((v) => v + 1);
            setGridApi(params.api);
            syncColumnFilters(params.api);
        }
    }, [applySavedGridStateToApi, fitColumnsToViewport, loadPersistedTableState, syncColumnFilters]);

    // Re-apply saved column state whenever columnDefs change (e.g. budget columns
    // appear/disappear), because AG Grid resets column state from columnDefs.
    useEffect(() => {
        if (!gridApi || !savedStateLoadedRef.current) {
            return;
        }
        if (hasSavedColumnWidthsRef.current || savedTableStateRef.current?.columnState?.length) {
            applySavedColumnStateToApi(gridApi, true);
        } else {
            fitColumnsToViewport(gridApi);
        }
    }, [gridApi, columnDefs, applySavedColumnStateToApi, fitColumnsToViewport, savedStateVersion]);

    return (
        <Box sx={{ height: '100%', minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
                <TextField
                    size="small"
                    placeholder={isProposalScope ? 'Quick filter proposals...' : 'Quick filter projects...'}
                    value={quickFilter}
                    onChange={(e) => setQuickFilter(e.target.value)}
                    sx={{ flex: 1, maxWidth: 400, minWidth: 200 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                />
                {!isProposalScope && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="projects-project-type-filter-label">Project type</InputLabel>
                    <Select
                        labelId="projects-project-type-filter-label"
                        label="Project type"
                        value={projectTypeFilter === '' ? '' : String(projectTypeFilter)}
                        onChange={(e) => {
                            const v = e.target.value;
                            setProjectTypeFilter(v === '' ? '' : Number(v));
                        }}
                    >
                        <MenuItem value="">All types</MenuItem>
                        {listProjectTypes.map((pt) => (
                            <MenuItem key={pt.id} value={String(pt.id)}>
                                {pt.description}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={showClosed || Boolean(clientNameFilter)}
                            disabled={Boolean(clientNameFilter)}
                            onChange={(e) => setShowClosed(e.target.checked)}
                        />
                    }
                    label="Show closed"
                    sx={{ ml: 0.5, mr: 0, userSelect: 'none' }}
                />
                {clientNameFilter && (
                    <Chip
                        label={`Client: ${clientNameFilter}`}
                        size="small"
                        onDelete={clearClientNameFilter}
                        sx={{ fontWeight: 600 }}
                    />
                )}
                {columnFiltersActive && (
                    <ClearGridFiltersButton onClick={() => clearColumnFilters(gridApi)} />
                )}
            </Box>

            <Paper
                elevation={0}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
                className={theme.palette.mode === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
            >
                <AgGridReact
                    theme="legacy"
                    rowData={filteredProjects}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={(params) => params.data.id.toString()}
                    rowHeight={45}
                    pagination={true}
                    paginationPageSize={20}
                    paginationPageSizeSelector={[10, 20, 50, 100]}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    onGridReady={onGridReady}
                    onGridSizeChanged={(params) => {
                        if (!savedStateLoadedRef.current) {
                            return;
                        }
                        if (!hasSavedColumnWidthsRef.current) {
                            fitColumnsToViewport(params.api);
                        }
                    }}
                    onColumnResized={(event: ColumnResizedEvent<Project>) => {
                        if (
                            !event.finished ||
                            !event.source ||
                            PROGRAMMATIC_COLUMN_RESIZE_SOURCES.has(event.source)
                        ) {
                            return;
                        }
                        persistTableState(event.api);
                    }}
                    onColumnMoved={(event: ColumnMovedEvent<Project>) => {
                        if (!event.finished) {
                            return;
                        }
                        persistTableState(event.api);
                    }}
                    onFilterChanged={(event: FilterChangedEvent<Project>) => {
                        syncColumnFilters(event.api);
                        if (!restoreCompleteRef.current || isApplyingSavedStateRef.current) {
                            return;
                        }
                        persistTableState(event.api);
                    }}
                />
            </Paper>
        </Box>
    );
};

export default ProjectsTableAgGrid;